/* Mock data for Tasks - moved from tasksMockData.ts */

import {
  OperationalDayTemplateDto,
  TaskCategoryDto,
  TaskPriorityDto,
  TaskStatusDto,
  TaskTemplateDto,
  TaskScheduleAssignmentDto,
  TaskScheduleDto,
  TaskSourceType,
  TaskDetailDto,
} from './tasksTypes';

// Helper function to create a date for today at a specific hour and minute
function todayAt(hour: number, minute: number = 0): string {
  const today = new Date();
  today.setHours(hour, minute, 0, 0);
  return today.toISOString();
}

class TasksMockStore {
  private _nextId = 1000;

  public taskCategories: TaskCategoryDto[] = [
    { 'id': 1, 'franchiseId': 65, 'name': 'Cleaning', 'description': 'Tasks related to hygiene and cleanliness' },
    { 'id': 2, 'franchiseId': 65, 'name': 'Inventory', 'description': 'Stock counts, receiving, rotation, and restocking' },
    { 'id': 3, 'franchiseId': 65, 'name': 'Staff', 'description': 'Employee, training, and staffing related tasks' },
    { 'id': 4, 'franchiseId': 65, 'name': 'Food Preparation', 'description': 'Tasks related to preparing, assembling, and cooking food' },
    { 'id': 5, 'franchiseId': 65, 'name': 'Food Safety', 'description': 'Temperature logs, allergen checks, and food safety compliance' },
    { 'id': 6, 'franchiseId': 65, 'name': 'Equipment Maintenance', 'description': 'Routine equipment checks, calibration, and minor fixes' },
    { 'id': 7, 'franchiseId': 65, 'name': 'Cash Handling', 'description': 'Till counts, safe drops, and banking tasks' },
    { 'id': 8, 'franchiseId': 65, 'name': 'Customer Service', 'description': 'Customer facing tasks related to service and hospitality' },
    { 'id': 9, 'franchiseId': 65, 'name': 'Opening Procedures', 'description': 'Tasks required to open the store' },
    { 'id': 10, 'franchiseId': 65, 'name': 'Closing Procedures', 'description': 'Tasks required to close and secure the store' },
    { 'id': 11, 'franchiseId': 65, 'name': 'Compliance', 'description': 'Regulatory, policy, and safety checks' },
    { 'id': 12, 'franchiseId': 65, 'name': 'Waste Management', 'description': 'Waste disposal, recycling, and food waste logging' },
    { 'id': 13, 'franchiseId': 65, 'name': 'Beverage Service', 'description': 'Drink preparation, barista work, and beverage equipment tasks' },
    { 'id': 14, 'franchiseId': 65, 'name': 'Merchandising', 'description': 'Promotional setup, displays, and planogram tasks' },
    { 'id': 15, 'franchiseId': 65, 'name': 'Facilities', 'description': 'Store infrastructure issues such as HVAC, lighting, and building maintenance' },
    { 'id': 16, 'franchiseId': 65, 'name': 'Operational Checks', 'description': 'Store readiness, quality checks, and operational standards reviews' },
    { 'id': 17, 'franchiseId': 65, 'name': 'Digital and Systems', 'description': 'POS updates, KDS resets, device troubleshooting, and system maintenance' },
    // Vapiano categories (franchiseId: 10)
    { 'id': 101, 'franchiseId': 10, 'name': 'Pasta Station', 'description': 'Fresh pasta preparation, cooking, and station management' },
    { 'id': 102, 'franchiseId': 10, 'name': 'Pizza Oven', 'description': 'Pizza oven operation, temperature control, and pizza preparation' },
    { 'id': 103, 'franchiseId': 10, 'name': 'Bar & Drinks', 'description': 'Bar service, cocktails, coffee, and beverage preparation' },
    { 'id': 104, 'franchiseId': 10, 'name': 'Herb Garden', 'description': 'Herb garden and olive tree maintenance and care' },
    { 'id': 105, 'franchiseId': 10, 'name': 'Hygiene & HACCP', 'description': 'Food safety, hygiene standards, and HACCP compliance' },
    { 'id': 106, 'franchiseId': 10, 'name': 'Customer Service', 'description': 'Guest experience, chip card system, and front-of-house tasks' },
    { 'id': 107, 'franchiseId': 10, 'name': 'Equipment Maintenance', 'description': 'Pasta rollers, pizza ovens, coffee machines, and general equipment' },
    { 'id': 108, 'franchiseId': 10, 'name': 'Chip Card System', 'description': 'Chip card reader checks, system health, and payment operations' },
    // Five Guys categories (franchiseId: 11)
    { 'id': 201, 'franchiseId': 11, 'name': 'Grill Station', 'description': 'Burger patty grilling, temperature control, and grill maintenance' },
    { 'id': 202, 'franchiseId': 11, 'name': 'Fry Station', 'description': 'Potato cutting, frying (double-cook method), and oil management' },
    { 'id': 203, 'franchiseId': 11, 'name': 'Toppings Prep', 'description': 'Hand-prepping fresh toppings: lettuce, onions, peppers, jalapeños' },
    { 'id': 204, 'franchiseId': 11, 'name': 'Peanut Safety', 'description': 'Peanut allergen management, signage, and oil safety' },
    { 'id': 205, 'franchiseId': 11, 'name': 'Shake Station', 'description': 'Milkshake preparation, mix-in stock, and blender maintenance' },
    { 'id': 206, 'franchiseId': 11, 'name': 'Customer Experience', 'description': 'Service quality, extra scoop compliance, and customer interaction' },
    { 'id': 207, 'franchiseId': 11, 'name': 'Equipment', 'description': 'Grill, fryer, shake blender, and Freestyle machine maintenance' },
    { 'id': 208, 'franchiseId': 11, 'name': 'Delivery & Storage', 'description': 'Potato and beef deliveries, cold storage, and supplier checks' },
  ];

  public taskPriorities: TaskPriorityDto[] = [
    { id: 1, franchiseId: 65, name: 'Low', description: 'Low priority' },
    { id: 2, franchiseId: 65, name: 'Medium', description: 'Medium priority' },
    { id: 3, franchiseId: 65, name: 'High', description: 'High priority' },
    { id: 4, franchiseId: 65, name: 'Emergency', description: 'Emergency - top tier priority requiring immediate attention' },
    // Vapiano priorities
    { id: 11, franchiseId: 10, name: 'Low', description: 'Low priority' },
    { id: 12, franchiseId: 10, name: 'Medium', description: 'Medium priority' },
    { id: 13, franchiseId: 10, name: 'High', description: 'High priority' },
    { id: 14, franchiseId: 10, name: 'Emergency', description: 'Emergency - immediate attention required' },
    // Five Guys priorities
    { id: 21, franchiseId: 11, name: 'Low', description: 'Low priority' },
    { id: 22, franchiseId: 11, name: 'Medium', description: 'Medium priority' },
    { id: 23, franchiseId: 11, name: 'High', description: 'High priority' },
    { id: 24, franchiseId: 11, name: 'Emergency', description: 'Emergency - immediate attention required' },
  ];

  public taskStatuses: TaskStatusDto[] = [
    { id: 1, franchiseId: 65, name: 'Open', description: 'Open' },
    { id: 2, franchiseId: 65, name: 'In Progress', description: 'In Progress' },
    { id: 3, franchiseId: 65, name: 'Pending Approval', description: 'Task completed and awaiting approval' },
    { id: 4, franchiseId: 65, name: 'Approved', description: 'Task approved as compliant' },
    { id: 5, franchiseId: 65, name: 'Rejected', description: 'Task rejected as non-compliant, requires re-work' },
    { id: 6, franchiseId: 65, name: 'Completed', description: 'Completed' },
    // Vapiano statuses
    { id: 11, franchiseId: 10, name: 'Open', description: 'Open' },
    { id: 12, franchiseId: 10, name: 'In Progress', description: 'In Progress' },
    { id: 13, franchiseId: 10, name: 'Pending Approval', description: 'Awaiting manager approval' },
    { id: 14, franchiseId: 10, name: 'Approved', description: 'Approved' },
    { id: 15, franchiseId: 10, name: 'Rejected', description: 'Rejected - requires re-work' },
    { id: 16, franchiseId: 10, name: 'Completed', description: 'Completed' },
    // Five Guys statuses
    { id: 21, franchiseId: 11, name: 'Open', description: 'Open' },
    { id: 22, franchiseId: 11, name: 'In Progress', description: 'In Progress' },
    { id: 23, franchiseId: 11, name: 'Pending Approval', description: 'Awaiting manager approval' },
    { id: 24, franchiseId: 11, name: 'Approved', description: 'Approved' },
    { id: 25, franchiseId: 11, name: 'Rejected', description: 'Rejected - requires re-work' },
    { id: 26, franchiseId: 11, name: 'Completed', description: 'Completed' },
  ];

  public operationalDays: OperationalDayTemplateDto[] = [
    {
      id: 1,
      name: 'Standard Weekday',
      description: 'Default weekday operational plan',
      effectiveFromDate: new Date().toISOString(),
      effectiveToDate: null,
      isActive: true,
    },
    {
      id: 2,
      name: 'Standard Saturday',
      description: 'Default Saturday operational plan',
      effectiveFromDate: new Date().toISOString(),
      effectiveToDate: null,
      isActive: true,
    },
    {
      id: 3,
      name: 'Standard Sunday',
      description: 'Default Sunday operational plan',
      effectiveFromDate: new Date().toISOString(),
      effectiveToDate: null,
      isActive: true,
    },
  ];

  public taskTemplates: TaskTemplateDto[] = [
    {
      id: 1,
      name: 'Daily Cleaning',
      description: 'Standard store cleaning checklist',
      taskCategoryId: 1,
      versions: [
        {
          id: 1,
          templateId: 1,
          versionNumber: 1,
          defaultTaskPriorityId: 2,
          expectedDurationMins: 30,
          isActive: true,
          isGlobalTemplate: true,
          steps: [
            { id: 1, order: 1, instruction: 'Sweep the floor in all customer areas', evidenceRequired: false, stepType: 'text', evidenceType: null },
            { id: 2, order: 2, instruction: 'Wipe down all tables and counters', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
            { id: 3, order: 3, instruction: 'Empty all trash bins and replace liners', evidenceRequired: false, stepType: 'text', evidenceType: null },
          ],
          requiresApproval: false,
          defaultApproverPersonaId: null,
          defaultApproverUserId: null,
        },
      ],
    },
    {
      id: 2,
      name: 'Open Store Checklist',
      description: 'Front-of-house and back-of-house opening procedures',
      taskCategoryId: 9,
      versions: [
        {
          id: 2,
          templateId: 2,
          versionNumber: 1,
          defaultTaskPriorityId: 3,
          expectedDurationMins: 45,
          isActive: true,
          isGlobalTemplate: true,
          steps: [
            { id: 4, order: 1, instruction: 'Disarm alarm and complete security check of entry points', evidenceRequired: false, stepType: 'text', evidenceType: null },
            { id: 5, order: 2, instruction: 'Turn on lights, POS, KDS and kitchen equipment', evidenceRequired: false, stepType: 'text', evidenceType: null },
            { id: 6, order: 3, instruction: 'Conduct quick store walkthrough to confirm cleanliness and readiness', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
            { id: 7, order: 4, instruction: 'Check cash float and safe contents against log sheet', evidenceRequired: true, stepType: 'text', evidenceType: 'text' },
          ],
          requiresApproval: false,
          defaultApproverPersonaId: null,
          defaultApproverUserId: null,
        },
      ],
    },
    {
      id: 3,
      name: 'Close Store Checklist',
      description: 'End-of-day closing and security procedures',
      taskCategoryId: 10,
      versions: [
        {
          id: 3,
          templateId: 3,
          versionNumber: 1,
          defaultTaskPriorityId: 3,
          expectedDurationMins: 40,
          isActive: true,
          isGlobalTemplate: true,
          steps: [
            { id: 8, order: 1, instruction: 'Complete dining area and service counter clean-down', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
            { id: 9, order: 2, instruction: 'Shut down and clean all cooking and holding equipment as per procedure', evidenceRequired: false, stepType: 'text', evidenceType: null },
            { id: 10, order: 3, instruction: 'Count tills, prepare safe drops and update cash log', evidenceRequired: true, stepType: 'text', evidenceType: 'text' },
            { id: 11, order: 4, instruction: 'Set alarm, lock all doors and confirm back door bolts engaged', evidenceRequired: false, stepType: 'text', evidenceType: null },
          ],
          requiresApproval: true,
          defaultApproverPersonaId: 1,
          defaultApproverUserId: null,
        },
      ],
    },
  ];

  public taskScheduleAssignments: TaskScheduleAssignmentDto[] = [
    {
      id: 1,
      taskScheduleId: 1,
      franchiseId: 65,
      scopeType: 'LOCATION',
      storeNumber: '004',
      personaId: null,
      userId: null,
      assignedAtUtc: new Date().toISOString(),
      assignedByUserId: null,
      unassignedAtUtc: null,
      escalationRules: [],
    },
    {
      id: 2,
      taskScheduleId: 2,
      franchiseId: 65,
      scopeType: 'LOCATION',
      storeNumber: '004',
      personaId: null,
      userId: null,
      assignedAtUtc: new Date().toISOString(),
      assignedByUserId: null,
      unassignedAtUtc: null,
      escalationRules: [],
    },
    {
      id: 3,
      taskScheduleId: 3,
      franchiseId: 65,
      scopeType: 'COLLEAGUE',
      storeNumber: '004',
      personaId: null,
      userId: null,
      assignedAtUtc: new Date().toISOString(),
      assignedByUserId: null,
      unassignedAtUtc: null,
      escalationRules: [],
    },
  ];

  public taskSchedules: TaskScheduleDto[] = [
    {
      id: 1,
      taskTemplateId: 1,
      templateTask: undefined as unknown as TaskTemplateDto,
      taskScheduleName: 'Weekly 9am cleaning',
      cronExpression: '0 0 9 * * 5',
      startDate: new Date().toISOString().substring(0, 10),
      endDate: null,
      nextRunDateUtc: new Date().toISOString(),
      lastGeneratedDateUtc: null,
      assignments: [],
      isActive: true,
    },
    {
      id: 2,
      taskTemplateId: 6,
      templateTask: undefined as unknown as TaskTemplateDto,
      taskScheduleName: 'Weekly dry store inventory – Monday 7am',
      cronExpression: '0 0 7 * * 1',
      startDate: new Date().toISOString().substring(0, 10),
      endDate: null,
      nextRunDateUtc: new Date().toISOString(),
      lastGeneratedDateUtc: null,
      assignments: [],
      isActive: true,
    },
    {
      id: 3,
      taskTemplateId: 8,
      templateTask: undefined as unknown as TaskTemplateDto,
      taskScheduleName: 'Weekly equipment maintenance – Wednesday 3pm',
      cronExpression: '0 0 15 * * 3',
      startDate: new Date().toISOString().substring(0, 10),
      endDate: null,
      nextRunDateUtc: new Date().toISOString(),
      lastGeneratedDateUtc: null,
      assignments: [],
      isActive: true,
    },
  ];

  public tasks: TaskDetailDto[] = [
    {
      id: 1,
      templateId: 1,
      title: 'Open store',
      description: 'Opening checklist',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 1,
      percentageComplete: 0,
      dueDateUtc: todayAt(6, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: 'Daily Cleaning',
      targetUserId: 1001,
      targetUserName: 'Alex Smith',
      targetRoleId: null,
      targetRoleName: null,
      steps: [
        { id: 1, order: 1, instruction: 'Sweep the floor in all customer areas', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 2, order: 2, instruction: 'Wipe down all tables and counters', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
        { id: 3, order: 3, instruction: 'Empty all trash bins and replace liners', evidenceRequired: false, stepType: 'text', evidenceType: null },
      ],
      evidence: [],
      history: [
        { id: 101, eventType: 'Created', oldValue: null, newValue: null, comment: 'Task created from schedule', createdAtUtc: todayAt(5, 0), createdByUserId: null },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 2,
      templateId: null,
      title: 'Count chilled inventory',
      description: 'Stock count for chilled section',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 2,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 2,
      percentageComplete: 50,
      dueDateUtc: todayAt(10, 0),
      startDateUtc: todayAt(9, 15),
      completedDateUtc: null,
      templateName: 'Inventory Count',
      targetUserId: 1002,
      targetUserName: 'Jordan Lee',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [
        { id: 201, evidenceType: 'text', evidenceText: 'Started count at 10:15am. Dairy section complete, moving to deli.', fileUrl: null, latitude: null, longitude: null, capturedAtUtc: new Date(Date.now() - 30 * 60 * 1000).toISOString(), capturedByUserId: 1001 },
      ],
      history: [
        { id: 102, eventType: 'Created', oldValue: null, newValue: null, comment: 'Ad-hoc task created by manager', createdAtUtc: new Date(Date.now() - 60 * 60 * 1000).toISOString(), createdByUserId: 1002 },
        { id: 103, eventType: 'StatusChanged', oldValue: 'Open', newValue: 'In Progress', comment: 'Starting inventory count', createdAtUtc: new Date(Date.now() - 45 * 60 * 1000).toISOString(), createdByUserId: 1001 },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'AdHoc' as TaskSourceType,
    },
    {
      id: 3,
      templateId: null,
      title: 'Deep clean preparation area',
      description: 'Weekly deep clean',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 3,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 1,
      percentageComplete: 100,
      dueDateUtc: todayAt(8, 0),
      startDateUtc: todayAt(7, 0),
      completedDateUtc: todayAt(7, 45),
      templateName: 'Deep Clean',
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [],
      evidence: [
        { id: 202, evidenceType: 'photo', evidenceText: 'Before photo – prep area surfaces', fileUrl: '/evidence/task3_before.jpg', latitude: 51.5074, longitude: -0.1278, capturedAtUtc: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), capturedByUserId: 1003 },
        { id: 203, evidenceType: 'photo', evidenceText: 'After photo – prep area fully cleaned', fileUrl: '/evidence/task3_after.jpg', latitude: 51.5074, longitude: -0.1278, capturedAtUtc: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(), capturedByUserId: 1003 },
      ],
      history: [
        { id: 105, eventType: 'Created', oldValue: null, newValue: null, comment: 'Weekly deep clean scheduled', createdAtUtc: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), createdByUserId: null },
        { id: 106, eventType: 'StatusChanged', oldValue: 'Open', newValue: 'In Progress', comment: 'Beginning deep clean', createdAtUtc: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), createdByUserId: 1003 },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 4,
      templateId: 2,
      title: 'Morning temperature checks',
      description: 'Check and log all fridge and freezer temperatures',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 6,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 5,
      percentageComplete: 100,
      dueDateUtc: todayAt(7, 0),
      startDateUtc: todayAt(6, 45),
      completedDateUtc: todayAt(6, 58),
      templateName: 'Temperature Log',
      targetUserId: 1001,
      targetUserName: 'Alex Smith',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 5,
      templateId: 3,
      title: 'Prep breakfast items',
      description: 'Prepare and stock breakfast sandwiches',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 6,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 4,
      percentageComplete: 100,
      dueDateUtc: todayAt(7, 30),
      startDateUtc: todayAt(7, 0),
      completedDateUtc: todayAt(7, 28),
      templateName: 'Food Prep',
      targetUserId: 1004,
      targetUserName: 'Maria Garcia',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 6,
      templateId: 4,
      title: 'Receive delivery from supplier',
      description: 'Check and put away morning delivery',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 2,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 2,
      percentageComplete: 30,
      dueDateUtc: todayAt(9, 0),
      startDateUtc: todayAt(8, 45),
      completedDateUtc: null,
      templateName: 'Receiving',
      targetUserId: 1002,
      targetUserName: 'Jordan Lee',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 7,
      templateId: null,
      title: 'Clean espresso machine',
      description: 'Daily backflush and wipe down',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 13,
      percentageComplete: 0,
      dueDateUtc: todayAt(11, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1005,
      targetUserName: 'Sam Taylor',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 8,
      templateId: null,
      title: 'Lunch rush prep',
      description: 'Stock grab-and-go items and prep lunch specials',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 4,
      percentageComplete: 0,
      dueDateUtc: todayAt(11, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1004,
      targetUserName: 'Maria Garcia',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 9,
      templateId: null,
      title: 'Midday temperature checks',
      description: 'Second temperature log of the day',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 5,
      percentageComplete: 0,
      dueDateUtc: todayAt(14, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: 'Temperature Log',
      targetUserId: 1001,
      targetUserName: 'Alex Smith',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 10,
      templateId: null,
      title: 'Till reconciliation',
      description: 'Count till and prepare banking',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 7,
      percentageComplete: 0,
      dueDateUtc: todayAt(15, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1001,
      targetUserName: 'Alex Smith',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 11,
      templateId: null,
      title: 'Afternoon restocking',
      description: 'Restock shelves and coolers for evening rush',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 2,
      percentageComplete: 0,
      dueDateUtc: todayAt(16, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1002,
      targetUserName: 'Jordan Lee',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 12,
      templateId: null,
      title: 'Equipment inspection',
      description: 'Check ovens, fridges and coffee machines',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 1,
      defaultPriorityId: 1,
      categoryId: 6,
      percentageComplete: 0,
      dueDateUtc: todayAt(17, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 13,
      templateId: null,
      title: 'Waste disposal and recycling',
      description: 'Empty waste bins and sort recycling',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 12,
      percentageComplete: 0,
      dueDateUtc: todayAt(20, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1006,
      targetUserName: 'Chris Brown',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 14,
      templateId: null,
      title: 'Closing cleaning',
      description: 'Full store cleaning before close',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 1,
      percentageComplete: 0,
      dueDateUtc: todayAt(21, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 15,
      templateId: null,
      title: 'End of day cash count',
      description: 'Final till count and safe deposit',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 7,
      percentageComplete: 0,
      dueDateUtc: todayAt(21, 45),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1001,
      targetUserName: 'Alex Smith',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 16,
      templateId: null,
      title: 'Fix broken door handle',
      description: 'Urgent repair needed for customer entrance',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 4,
      defaultPriorityId: 4,
      categoryId: 15,
      percentageComplete: 0,
      dueDateUtc: todayAt(13, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'AdHoc' as TaskSourceType,
    },
    {
      id: 17,
      templateId: null,
      title: 'Customer complaint follow-up',
      description: 'Call customer regarding order issue',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 3,
      defaultPriorityId: 3,
      categoryId: 8,
      percentageComplete: 0,
      dueDateUtc: todayAt(14, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1001,
      targetUserName: 'Alex Smith',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'AdHoc' as TaskSourceType,
    },
    {
      id: 18,
      templateId: null,
      title: 'System software update',
      description: 'Update POS system during quiet period',
      storeNumber: '004',
      storeName: 'Downtown Express',
      statusId: 1,
      priorityId: 2,
      defaultPriorityId: 2,
      categoryId: 17,
      percentageComplete: 0,
      dueDateUtc: todayAt(15, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 1001,
      targetUserName: 'Alex Smith',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'System' as TaskSourceType,
    },
    // ---- Vapiano Tasks (storeNumber: VAP001, VAP002, VAP003) ----
    {
      id: 100,
      templateId: null,
      title: 'Morning pasta dough preparation',
      description: 'Prepare fresh egg pasta dough for the day — tagliatelle, spaghetti, and penne. Minimum 15kg total across all formats.',
      storeNumber: 'VAP001',
      storeName: 'Vapiano - Great Portland Street',
      statusId: 12,
      priorityId: 13,
      defaultPriorityId: 13,
      categoryId: 101,
      percentageComplete: 60,
      dueDateUtc: todayAt(9, 0),
      startDateUtc: todayAt(7, 30),
      completedDateUtc: null,
      templateName: 'Fresh Pasta Prep',
      targetUserId: 2001,
      targetUserName: 'Marco Bianchi',
      targetRoleId: null,
      targetRoleName: null,
      steps: [
        { id: 101, order: 1, instruction: 'Weigh and sift 00 flour (10kg semolina, 5kg plain)', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 102, order: 2, instruction: 'Crack eggs and mix dough in pasta machine', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 103, order: 3, instruction: 'Rest dough for 30 minutes minimum', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 104, order: 4, instruction: 'Roll and cut into tagliatelle, spaghetti, penne portions', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
        { id: 105, order: 5, instruction: 'Portion into service containers and label with time', evidenceRequired: false, stepType: 'text', evidenceType: null },
      ],
      evidence: [],
      history: [
        { id: 201, eventType: 'Created', oldValue: null, newValue: null, comment: 'Scheduled task created', createdAtUtc: todayAt(5, 0), createdByUserId: null },
        { id: 202, eventType: 'StatusChanged', oldValue: 'Open', newValue: 'In Progress', comment: 'Starting dough prep', createdAtUtc: todayAt(7, 30), createdByUserId: 2001 },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 101,
      templateId: null,
      title: 'Pizza oven temperature calibration',
      description: 'Check and calibrate pizza oven to 400°C. Log temperature readings. Clean oven floor of char residue.',
      storeNumber: 'VAP001',
      storeName: 'Vapiano - Great Portland Street',
      statusId: 11,
      priorityId: 13,
      defaultPriorityId: 13,
      categoryId: 102,
      percentageComplete: 0,
      dueDateUtc: todayAt(10, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 2002,
      targetUserName: 'Luca Ferretti',
      targetRoleId: null,
      targetRoleName: null,
      steps: [
        { id: 106, order: 1, instruction: 'Turn on oven and allow 45 min warm-up', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 107, order: 2, instruction: 'Check temperature with infrared thermometer (must read 380-420°C)', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
        { id: 108, order: 3, instruction: 'Clean oven floor with copper brush', evidenceRequired: false, stepType: 'text', evidenceType: null },
      ],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 102,
      templateId: null,
      title: 'Herb garden morning watering',
      description: 'Water all basil plants, check rosemary and thyme, inspect olive trees for leaf health. Replace any wilting plants.',
      storeNumber: 'VAP001',
      storeName: 'Vapiano - Great Portland Street',
      statusId: 16,
      priorityId: 12,
      defaultPriorityId: 12,
      categoryId: 104,
      percentageComplete: 100,
      dueDateUtc: todayAt(7, 30),
      startDateUtc: todayAt(7, 0),
      completedDateUtc: todayAt(7, 20),
      templateName: null,
      targetUserId: 2003,
      targetUserName: 'Elena Rossi',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [
        { id: 301, evidenceType: 'photo', evidenceText: 'All herbs watered and healthy', fileUrl: null, latitude: null, longitude: null, capturedAtUtc: todayAt(7, 15), capturedByUserId: 2003 },
      ],
      history: [
        { id: 203, eventType: 'StatusChanged', oldValue: 'Open', newValue: 'Completed', comment: 'All plants watered, one basil replaced', createdAtUtc: todayAt(7, 20), createdByUserId: 2003 },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 103,
      templateId: null,
      title: 'Chip card system health check',
      description: 'Test all chip card readers at pasta, pizza, salad, and bar stations. Verify NFC tap-to-pay functionality at exit kiosk.',
      storeNumber: 'VAP001',
      storeName: 'Vapiano - Great Portland Street',
      statusId: 11,
      priorityId: 13,
      defaultPriorityId: 13,
      categoryId: 108,
      percentageComplete: 0,
      dueDateUtc: todayAt(10, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 2004,
      targetUserName: 'James Fletcher',
      targetRoleId: null,
      targetRoleName: null,
      steps: [
        { id: 109, order: 1, instruction: 'Test pasta station reader with staff card', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 110, order: 2, instruction: 'Test pizza station reader', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 111, order: 3, instruction: 'Test salad station reader', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 112, order: 4, instruction: 'Test bar station reader', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 113, order: 5, instruction: 'Test exit kiosk NFC tap-to-pay', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
      ],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 104,
      templateId: null,
      title: 'Bar opening: coffee machine & Aperol prep',
      description: 'Start espresso machine flush cycle, prep Aperol Spritz garnishes (fresh orange slices), check prosecco and soda water stock.',
      storeNumber: 'VAP001',
      storeName: 'Vapiano - Great Portland Street',
      statusId: 12,
      priorityId: 12,
      defaultPriorityId: 12,
      categoryId: 103,
      percentageComplete: 40,
      dueDateUtc: todayAt(10, 0),
      startDateUtc: todayAt(9, 30),
      completedDateUtc: null,
      templateName: null,
      targetUserId: 2005,
      targetUserName: 'Giulia Romano',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 105,
      templateId: null,
      title: 'HACCP temperature log — cold storage',
      description: 'Record temperatures of all walk-in chillers and freezers. Flag any readings outside safe range (0-5°C chiller, -18°C freezer).',
      storeNumber: 'VAP001',
      storeName: 'Vapiano - Great Portland Street',
      statusId: 11,
      priorityId: 14,
      defaultPriorityId: 14,
      categoryId: 105,
      percentageComplete: 0,
      dueDateUtc: todayAt(8, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: true,
      approverUserId: 2004,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 106,
      templateId: null,
      title: 'Salad bar ingredient prep',
      description: 'Prep all salad station ingredients: wash and cut lettuce, slice tomatoes, prepare dressings, refill olive oil stations.',
      storeNumber: 'VAP002',
      storeName: 'Vapiano - Bankside',
      statusId: 11,
      priorityId: 12,
      defaultPriorityId: 12,
      categoryId: 101,
      percentageComplete: 0,
      dueDateUtc: todayAt(9, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 2006,
      targetUserName: 'Sofia Marchetti',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 107,
      templateId: null,
      title: 'Pasta roller deep clean',
      description: 'Disassemble pasta roller, clean all components, re-assemble and test with small dough batch. Log maintenance in equipment register.',
      storeNumber: 'VAP002',
      storeName: 'Vapiano - Bankside',
      statusId: 12,
      priorityId: 12,
      defaultPriorityId: 12,
      categoryId: 107,
      percentageComplete: 50,
      dueDateUtc: todayAt(14, 0),
      startDateUtc: todayAt(12, 0),
      completedDateUtc: null,
      templateName: null,
      targetUserId: 2007,
      targetUserName: 'Antonio Vitale',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 108,
      templateId: null,
      title: 'Open kitchen guest interaction training',
      description: 'Conduct 15-min refresher with all station chefs on engaging with guests who approach the open kitchen. Practice explaining dishes being prepared.',
      storeNumber: 'VAP003',
      storeName: 'Vapiano - Manchester Deansgate',
      statusId: 11,
      priorityId: 12,
      defaultPriorityId: 12,
      categoryId: 106,
      percentageComplete: 0,
      dueDateUtc: todayAt(11, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 109,
      templateId: null,
      title: 'Evening Carbonara ingredient restock',
      description: 'Check and restock Carbonara supplies for evening service: guanciale, Pecorino Romano, egg yolks, black pepper. Minimum 50 portions worth.',
      storeNumber: 'VAP003',
      storeName: 'Vapiano - Manchester Deansgate',
      statusId: 11,
      priorityId: 13,
      defaultPriorityId: 13,
      categoryId: 101,
      percentageComplete: 0,
      dueDateUtc: todayAt(16, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 2008,
      targetUserName: 'Davide Conti',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    // ---- Five Guys Tasks (storeNumber: FG001, FG002, FG003) ----
    {
      id: 200,
      templateId: null,
      title: 'Morning potato delivery & inspection',
      description: 'Receive potato delivery, check quality (no green, no soft spots), verify weight matches order. Record supplier and farm origin on the board.',
      storeNumber: 'FG001',
      storeName: 'Five Guys - Covent Garden',
      statusId: 22,
      priorityId: 23,
      defaultPriorityId: 23,
      categoryId: 208,
      percentageComplete: 70,
      dueDateUtc: todayAt(8, 0),
      startDateUtc: todayAt(7, 15),
      completedDateUtc: null,
      templateName: 'Potato Delivery',
      targetUserId: 3001,
      targetUserName: 'Ryan Mitchell',
      targetRoleId: null,
      targetRoleName: null,
      steps: [
        { id: 201, order: 1, instruction: 'Check delivery temperature log from truck', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 202, order: 2, instruction: 'Inspect 10% sample of potatoes for quality', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
        { id: 203, order: 3, instruction: 'Verify weight matches order (±2% tolerance)', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 204, order: 4, instruction: 'Write farm origin on the board for customers', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
      ],
      evidence: [
        { id: 401, evidenceType: 'photo', evidenceText: 'Potatoes look great - Farm: Lincolnshire Gold, Farmer: J. Wilson', fileUrl: null, latitude: null, longitude: null, capturedAtUtc: todayAt(7, 30), capturedByUserId: 3001 },
      ],
      history: [
        { id: 301, eventType: 'Created', oldValue: null, newValue: null, comment: 'Scheduled delivery task', createdAtUtc: todayAt(5, 0), createdByUserId: null },
        { id: 302, eventType: 'StatusChanged', oldValue: 'Open', newValue: 'In Progress', comment: 'Delivery arrived', createdAtUtc: todayAt(7, 15), createdByUserId: 3001 },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 201,
      templateId: null,
      title: 'Grill calibration & patty temperature test',
      description: 'Calibrate flat-top grill to 375°F. Cook test patty and verify internal temp reaches 160°F (71°C). Log results.',
      storeNumber: 'FG001',
      storeName: 'Five Guys - Covent Garden',
      statusId: 21,
      priorityId: 24,
      defaultPriorityId: 24,
      categoryId: 201,
      percentageComplete: 0,
      dueDateUtc: todayAt(9, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 3002,
      targetUserName: 'Jake Williams',
      targetRoleId: null,
      targetRoleName: null,
      steps: [
        { id: 205, order: 1, instruction: 'Turn on grill and heat for 20 minutes', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 206, order: 2, instruction: 'Check surface temp with infrared thermometer (375°F ±10°F)', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
        { id: 207, order: 3, instruction: 'Cook test patty for 4 mins per side', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 208, order: 4, instruction: 'Probe internal temp — must reach 160°F (71°C)', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
      ],
      evidence: [],
      history: [],
      requiresApproval: true,
      approverUserId: 3001,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 202,
      templateId: null,
      title: 'Morning toppings prep: hand-tear lettuce & slice tomatoes',
      description: 'Hand-tear iceberg lettuce (no knives!), slice tomatoes, dice onions, slice mushrooms, cut jalapeños. Minimum 4 hours of service stock.',
      storeNumber: 'FG001',
      storeName: 'Five Guys - Covent Garden',
      statusId: 22,
      priorityId: 23,
      defaultPriorityId: 23,
      categoryId: 203,
      percentageComplete: 80,
      dueDateUtc: todayAt(10, 0),
      startDateUtc: todayAt(8, 30),
      completedDateUtc: null,
      templateName: null,
      targetUserId: 3003,
      targetUserName: 'Priya Patel',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [
        { id: 303, eventType: 'StatusChanged', oldValue: 'Open', newValue: 'In Progress', comment: 'Starting toppings prep', createdAtUtc: todayAt(8, 30), createdByUserId: 3003 },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 203,
      templateId: null,
      title: 'Peanut oil quality check & fryer filter',
      description: 'Test peanut oil quality with test strip (must pass colour test). Filter all fryer oil. Check and clean fryer baskets. Record oil age.',
      storeNumber: 'FG001',
      storeName: 'Five Guys - Covent Garden',
      statusId: 21,
      priorityId: 23,
      defaultPriorityId: 23,
      categoryId: 204,
      percentageComplete: 0,
      dueDateUtc: todayAt(9, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 3004,
      targetUserName: 'Callum Davies',
      targetRoleId: null,
      targetRoleName: null,
      steps: [
        { id: 209, order: 1, instruction: 'Test oil quality with indicator strip (compare to colour chart)', evidenceRequired: true, stepType: 'text', evidenceType: 'photo' },
        { id: 210, order: 2, instruction: 'Filter oil through fryer filtration system', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 211, order: 3, instruction: 'Record oil age on tracking sheet (replace after 5 days max)', evidenceRequired: false, stepType: 'text', evidenceType: null },
      ],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 204,
      templateId: null,
      title: 'Peanut allergen signage audit',
      description: 'Walk the store and verify all allergen signage is in place: entrance signs (A3), counter cards, peanut box warning, digital matrix in crew area.',
      storeNumber: 'FG001',
      storeName: 'Five Guys - Covent Garden',
      statusId: 26,
      priorityId: 24,
      defaultPriorityId: 24,
      categoryId: 204,
      percentageComplete: 100,
      dueDateUtc: todayAt(8, 30),
      startDateUtc: todayAt(8, 0),
      completedDateUtc: todayAt(8, 25),
      templateName: null,
      targetUserId: 3001,
      targetUserName: 'Ryan Mitchell',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [
        { id: 402, evidenceType: 'photo', evidenceText: 'All 4 signage points confirmed in place', fileUrl: null, latitude: null, longitude: null, capturedAtUtc: todayAt(8, 20), capturedByUserId: 3001 },
      ],
      history: [
        { id: 304, eventType: 'StatusChanged', oldValue: 'Open', newValue: 'Completed', comment: 'All signage in place and visible', createdAtUtc: todayAt(8, 25), createdByUserId: 3001 },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 205,
      templateId: null,
      title: 'Milkshake station opening: blender check & mix-in stock',
      description: 'Test all 3 blenders, check mix-in stock levels (minimum 2 full containers of each flavour), confirm Jimmy\'s Coffee LTO signage is displayed.',
      storeNumber: 'FG001',
      storeName: 'Five Guys - Covent Garden',
      statusId: 21,
      priorityId: 22,
      defaultPriorityId: 22,
      categoryId: 205,
      percentageComplete: 0,
      dueDateUtc: todayAt(10, 30),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 3005,
      targetUserName: 'Amelia Green',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 206,
      templateId: null,
      title: 'Afternoon lettuce re-prep (7pm freshness rule)',
      description: 'Re-prep hand-torn lettuce for evening service. Discard existing lettuce prepped before 3pm. Hand-tear fresh — never cut with a knife.',
      storeNumber: 'FG002',
      storeName: 'Five Guys - Birmingham Bullring',
      statusId: 21,
      priorityId: 22,
      defaultPriorityId: 22,
      categoryId: 203,
      percentageComplete: 0,
      dueDateUtc: todayAt(19, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: 3006,
      targetUserName: 'Liam Scott',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
    {
      id: 207,
      templateId: null,
      title: 'Extra scoop compliance spot-check',
      description: 'Observe 10 consecutive fry orders and verify crew is giving the extra scoop every time. Record compliance % and coach any misses.',
      storeNumber: 'FG002',
      storeName: 'Five Guys - Birmingham Bullring',
      statusId: 21,
      priorityId: 23,
      defaultPriorityId: 23,
      categoryId: 206,
      percentageComplete: 0,
      dueDateUtc: todayAt(13, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [
        { id: 212, order: 1, instruction: 'Observe 10 fry orders without crew knowing', evidenceRequired: false, stepType: 'text', evidenceType: null },
        { id: 213, order: 2, instruction: 'Record how many received extra scoop (target: 10/10)', evidenceRequired: true, stepType: 'text', evidenceType: 'text' },
        { id: 214, order: 3, instruction: 'Coach any crew who missed — remind it is mandatory', evidenceRequired: false, stepType: 'text', evidenceType: null },
      ],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 208,
      templateId: null,
      title: 'Double-cook fry procedure audit',
      description: 'Verify crew is following the Five Guys double-cook method: first cook at 325°F until limp, shake, rest, second cook at 375°F until golden.',
      storeNumber: 'FG003',
      storeName: 'Five Guys - Leeds Trinity',
      statusId: 21,
      priorityId: 23,
      defaultPriorityId: 23,
      categoryId: 202,
      percentageComplete: 0,
      dueDateUtc: todayAt(12, 0),
      startDateUtc: null,
      completedDateUtc: null,
      templateName: null,
      targetUserId: null,
      targetUserName: null,
      targetRoleId: 1,
      targetRoleName: 'Store Manager',
      steps: [],
      evidence: [],
      history: [],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'Scheduled' as TaskSourceType,
    },
    {
      id: 209,
      templateId: null,
      title: 'Beef patty delivery & cold storage check',
      description: 'Receive fresh beef delivery (never frozen). Check delivery temp is below 4°C. Store immediately in walk-in. Record batch numbers.',
      storeNumber: 'FG003',
      storeName: 'Five Guys - Leeds Trinity',
      statusId: 22,
      priorityId: 24,
      defaultPriorityId: 24,
      categoryId: 208,
      percentageComplete: 50,
      dueDateUtc: todayAt(7, 30),
      startDateUtc: todayAt(7, 0),
      completedDateUtc: null,
      templateName: null,
      targetUserId: 3007,
      targetUserName: 'Tom Richardson',
      targetRoleId: null,
      targetRoleName: null,
      steps: [],
      evidence: [
        { id: 403, evidenceType: 'text', evidenceText: 'Delivery arrived at 7:05am, temp 2.1°C — within spec. Batch #BF20240615-003', fileUrl: null, latitude: null, longitude: null, capturedAtUtc: todayAt(7, 10), capturedByUserId: 3007 },
      ],
      history: [],
      requiresApproval: true,
      approverUserId: 3001,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'OperationalDay' as TaskSourceType,
    },
  ];

  constructor() {
    // Wire template reference and assignments after construction
    this.taskSchedules.forEach((schedule) => {
      const template = this.taskTemplates.find((t) => t.id === schedule.taskTemplateId);
      if (template) {
        schedule.templateTask = template;
      }
      schedule.assignments = this.taskScheduleAssignments.filter(
        (assignment) => assignment.taskScheduleId === schedule.id,
      );
    });
  }

  public nextId(): number {
    return this._nextId++;
  }
}

export const tasksMockStore = new TasksMockStore();
